<template>
  <div class="pb-2">
    <template v-for="(option, index) in options" :key="index">
      <component
        :is="option.component"
        v-if="(!option.features || $features.all(option.features)) && (option.isIntegration ? option.data.enabled : true)"
        :ref="option.guid"

        v-model:selected="_selected"
        v-model:processing="_processing"
        :option="option"
        v-model:option-data="option.data"
        v-bind="containerProps"
        v-on="events"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import Provider from './PaymentProvider'

import { useDiscountModelStore } from '~/stores/useDiscountModelStore'
import { usePaymentModelStore } from '~/stores/usePaymentModelStore'

const { $api, $notification } = useNuxtApp()

const discountModelStore = useDiscountModelStore()
const paymentModelStore = usePaymentModelStore()

interface Props {
  selected?: any
  amount: number | string
  tip?: number | string | null
  order?: any
}

const props = withDefaults(defineProps<Props>(), {
  selected: null,
  tip: null,
  order: null,
})

const emit = defineEmits<{
  (e: 'update:selected', value: any): void
  (e: 'update:processing', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}>()

const instance = getCurrentInstance()

const activeIntegrations = ref<any>(null)
const customerPaymentMethods = ref<any>(null)

const paymentModels = computed(() => paymentModelStore.all)

const options = ref<any[]>([])

function buildOptions() {
  const provider = new Provider({
    activeIntegrations: activeIntegrations.value,
    customerPaymentMethods: customerPaymentMethods.value,
    paymentModels: paymentModels.value,
  })
  options.value = provider.options
}

watch([activeIntegrations, customerPaymentMethods, paymentModels], buildOptions, { deep: true, immediate: true })

const containerProps = computed(() => ({
  amount: props.amount,
  tip: props.tip,
  order: props.order,
  processing: undefined,
}))

const events = computed(() => ({
  handleSuccess,
  handleError,
}))

const _selected = computed({
  get: () => props.selected?.guid,
  set: (value: string) => {
    const selected = options.value.find((option: any) => option.guid === value)
    emit('update:selected', selected)
  },
})

const _processing = computed({
  get: () => false,
  set: (value: boolean) => emit('update:processing', value),
})

onMounted(() => {
  fetchIntegrations()
  fetchCustomerPaymentMethods()

  discountModelStore.sync()
  paymentModelStore.sync()
})

async function fetchIntegrations() {
  try {
    const integrations = await $api.integration.active()
    activeIntegrations.value = integrations
  } catch (e: any) {
    console.error(e)
    $notification.error({ errors: e?.errors })
  }
}

async function fetchCustomerPaymentMethods() {
  try {
    const customer = props.order.customer
    const customerId = customer?.idCustomer?.toString() || customer?.objectID?.()
    if (customerId) {
      const result = await $api.customer.paymentMethods(customerId)
      customerPaymentMethods.value = result.data ?? null
    }
  } catch (e: any) {
    console.error(e)
    $notification.error({ errors: e?.errors })
  }
}

async function process() {
  _processing.value = true

  try {
    const refs = instance?.refs as any
    const selected = refs[_selected.value]?.[0]
    return await selected.process()
  } catch (e: any) {
    if (e.errors) {
      throw e
    }
    throw new Error(e)
  }
}

function refresh() {
  fetchIntegrations()
  fetchCustomerPaymentMethods()
  discountModelStore.sync({ force: true })
}

function handleSuccess() {
  emit('success')
}

function handleError() {
  emit('error')
}

defineExpose({ process, refresh })
</script>
